<template>
  <div>
    <b-row
      v-if="patient"
      class="match-height"
    >

      <b-col md="12">
        <b-card
          :title="patient.surname + ' ' + patient.name"
          :sub-title="$t(`order.patient-info.${patient.sex}`)"
        >
          <b-row>
            <b-col>

              <div class="w-full">
                <validation-provider
                  ref="peselValidationProvider"
                  #default="{ errors }"
                  :name="$t('order.patient-info.pesel')"
                  :rules="peselValidationRules"
                >
                  <label>
                    {{ $t('order.patient-info.pesel') }}
                  </label>
                  <b-form-input
                    v-model="patient.pesel"
                    type="text"
                    :name="$t('order.patient-info.pesel')"
                    :disabled="patient.patientData.noPesel"
                    @change="handlePeselChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="w-full">
                <b-form-checkbox
                  v-model="patient.patientData.noPesel"
                  class="my-1"
                  @change="handleNoPeselChange"
                >
                  {{ $t('order.patient-info.no-pesel') }}
                </b-form-checkbox>
              </div>

              <div class="w-full">
                <b-form-checkbox
                  v-model="patient.patientData.requiresCaregiver"
                  class="my-1"
                  @change="commitOrder()"
                >
                  {{ $t('order.patient-info.caregiver.required') }}
                </b-form-checkbox>
              </div>

            </b-col>

            <b-col
              cols="12"
              md="6"
              class="pb-1"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.age-in-years')"
                rules="max_value:120"
              >
                <label>
                  {{ $t('order.patient-info.age-in-years') }}
                </label>
                <b-input-group class="mb-50">
                  <b-form-input
                    v-model="patient.age"
                    type="number"
                    maxlength="3"
                    :placeholder="$t('order.patient-info.age-in-years')"
                    autocomplete="off"
                    :disabled="isAgeAndSexDisabled"
                    @change="commitOrder"
                  />
                  <b-input-group-append v-if="!isAgeAndSexDisabled">
                    <b-button
                      class="px-75"
                      variant="outline-secondary"
                      @click="convertFromAnnual(patient)"
                    >
                      <feather-icon icon="RefreshCwIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.weight')"
                rules="max_value:500"
              >
                <label>
                  {{ $t('order.patient-info.weight') }}
                </label>
                <b-input-group>
                  <b-form-input
                    v-model="patient.weight"
                    type="text"
                    inputmode="decimal"
                    :placeholder="$t('order.patient-info.weight')"
                    autocomplete="off"
                    @change="commitOrder"
                  />
                  <b-input-group-append is-text>
                    kg
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <b-col
        v-if="patient.patientData.requiresCaregiver"
        md="12"
      >
        <b-card :title="$t('order.patient-info.caregiver.title')">
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="pb-1"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.caregiver.name')"
                rules="max:50"
              >
                <label>
                  {{ $t('order.patient-info.caregiver.name') }}
                </label>
                <b-form-input
                  v-model="patient.patientData.caregiver.name"
                  type="text"
                  maxlength="50"
                  :placeholder="$t('order.patient-info.caregiver.name')"
                  autocomplete="off"
                  @change="commitOrder"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="pb-1"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.caregiver.surname')"
                rules="max:50"
              >
                <label>
                  {{ $t('order.patient-info.caregiver.surname') }}
                </label>
                <b-form-input
                  v-model="patient.patientData.caregiver.surname"
                  type="text"
                  maxlength="50"
                  :placeholder="$t('order.patient-info.caregiver.surname')"
                  autocomplete="off"
                  @change="commitOrder"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="pb-1"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.caregiver.pesel')"
                :rules="peselValidationRules"
              >
                <label>
                  {{ $t('order.patient-info.caregiver.pesel') }}
                </label>
                <b-form-input
                  v-model="patient.patientData.caregiver.pesel"
                  type="text"
                  :name="$t('order.patient-info.caregiver.pesel')"
                  @change="commitOrder"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="pb-1"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('order.patient-info.caregiver.phone')"
                :rules="caregiverPhoneValidationRules"
              >
                <label>
                  {{ $t('order.patient-info.caregiver.phone') }} *
                </label>
                <b-form-input
                  v-model="patient.patientData.caregiver.phone"
                  type="text"
                  maxlength="20"
                  :placeholder="$t('order.patient-info.caregiver.phone')"
                  autocomplete="off"
                  @change="commitOrder"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <b-col md="6">
        <b-card :title="$t('order.patient-info.transport-status.title')">
          <b-form-radio-group
            v-model="patient.patientData.transportStatus"
            stacked
            @change="commitOrder"
          >
            <b-form-radio
              class="my-25"
              value="patientRemained"
            >
              {{ $t('order.patient-info.transport-status.patientRemained') }}
            </b-form-radio>

            <b-form-radio
              class="my-25"
              value="patientTransported"
            >
              {{ $t('order.patient-info.transport-status.patientTransported') }}
            </b-form-radio>

            <b-form-radio
              class="my-25"
              value="patientTransferredToZrm"
            >
              {{ $t('order.patient-info.transport-status.patientTransferredToZrm') }}
            </b-form-radio>

            <b-form-radio
              class="my-25"
              value="patientAbsent"
            >
              {{ $t('order.patient-info.transport-status.patientAbsent') }}
            </b-form-radio>

            <b-form-radio
              class="my-25"
              value="patientRefused"
            >
              {{ $t('order.patient-info.transport-status.patientRefused') }}
            </b-form-radio>
          </b-form-radio-group>

        </b-card>
      </b-col>

      <b-col md="6">
        <b-card :title="$t('order.patient-info.valuables.title')">

          <b-form-checkbox
            v-model="patient.patientData.valuables.identityDocument"
            class="my-50"
            @change="commitOrder"
          >
            {{ $t('order.patient-info.valuables.identity-document') }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="patient.patientData.valuables.other"
            class="my-50"
            @change="patient.patientData.valuables.otherItems = null; commitOrder()"
          >
            {{ $t('order.patient-info.valuables.other') }}
          </b-form-checkbox>

          <div v-if="patient.patientData.valuables.other">
            <label>
              {{ $t('order.patient-info.valuables.other-items') }}
            </label>
            <b-form-textarea
              v-model="patient.patientData.valuables.otherItems"
              :name="$t('order.patient-info.valuables.other-items')"
              :disabled="!patient.patientData.valuables.other"
              @change="commitOrder"
            />
          </div>

        </b-card>
      </b-col>
    </b-row>
    <p
      v-else
      class="text-muted"
    >
      {{ $t('order.patient-info.list.empty') }}
    </p>
  </div>
</template>
<script>

import {
  BButton, BCard, BCol, BFormCheckbox, BFormInput, BFormRadio, BFormRadioGroup, BFormTextarea, BInputGroup, BInputGroupAppend, BRow,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import moment from 'moment'
import { resolvePatientDemographicsFromPesel } from '@/helpers/pesel'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    BFormCheckbox,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    ValidationProvider,
  },
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
    patient: {
      type: Object,
      default: null,
    },
    commitOrder: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    organizationConfig() {
      return this.$store.getters['appConfig/config'] || {}
    },
    peselValidationRules() {
      if (this.patient?.patientData?.noPesel) {
        return ''
      }

      return this.organizationConfig?.frontendValidation?.order?.patientInfo?.peselRules ?? 'pesel'
    },
    caregiverPhoneValidationRules() {
      return this.patient?.patientData?.requiresCaregiver ? 'required|max:20' : 'max:20'
    },
    resolvedDemographicsFromPesel() {
      if (this.patient?.patientData?.noPesel) {
        return null
      }

      return resolvePatientDemographicsFromPesel(this.patient?.pesel, this.order?.startAt)
    },
    isAgeAndSexDisabled() {
      return !!this.resolvedDemographicsFromPesel
    },
  },
  watch: {
    patient: {
      immediate: true,
      handler(patient) {
        this.ensureCaregiverStructure(patient)
      },
    },
  },
  methods: {
    handlePeselChange() {
      this.syncDemographicsFromPesel()
      this.commitOrder()
    },
    handleNoPeselChange() {
      this.patient.pesel = null
      this.resetPeselValidation()
      this.commitOrder()
    },
    syncDemographicsFromPesel() {
      const demographics = this.resolvedDemographicsFromPesel
      if (!demographics) {
        return
      }

      this.$set(this.patient, 'age', demographics.age)
      this.$set(this.patient, 'sex', demographics.sex)
    },
    resetPeselValidation() {
      this.$nextTick(() => {
        const provider = this.$refs.peselValidationProvider
        if (provider && typeof provider.reset === 'function') {
          provider.reset()
        }
      })
    },
    ensureCaregiverStructure(patient) {
      if (!patient) {
        return
      }

      if (!patient.patientData || typeof patient.patientData !== 'object') {
        this.$set(patient, 'patientData', {})
      }

      if (typeof patient.patientData.requiresCaregiver !== 'boolean') {
        this.$set(patient.patientData, 'requiresCaregiver', false)
      }

      if (!patient.patientData.caregiver || typeof patient.patientData.caregiver !== 'object') {
        this.$set(patient.patientData, 'caregiver', {
          name: null,
          surname: null,
          pesel: null,
          phone: null,
        })
      }
    },
    convertFromAnnual(patient) {
      if (!patient || patient.age === null || patient.age === undefined || patient.age === '') {
        return
      }

      const ageString = patient.age.toString()
      const yearShort = parseInt(moment().format('YY'), 10)
      const yearFull = parseInt(moment().format('YYYY'), 10)
      let nextAge

      if (ageString.length < 3) {
        nextAge = yearShort < patient.age
          ? yearShort - patient.age + 100
          : yearShort - patient.age
      } else {
        nextAge = yearFull - patient.age
      }

      this.$set(patient, 'age', nextAge)
      this.commitOrder()
    },
  },
}
</script>
